import { createSelector } from "reselect";
import { Map, Record as ImmutableRecord, List } from "immutable";
import { format, subDays, addDays, parseISO } from "date-fns";

import {
  EXTRA_SERVICES_B2B,
  FREQUENCY,
  PREMISES,
  PREMISES_BOOLEAN_EQUIPMENTS_OPTIONS,
  PREMISES_COUNTABLE_EQUIPMENTS_OPTIONS,
  Task,
} from "@typings/b2b";
import { CALENDAR_WEEK_SHIFT, HourItem, PricePlan } from "@typings/booking";

import mapToList from "@services/MapToList";
import { DATE_FORMAT, newDateSafe } from "@services/DateFormatter";
import { intlNumberFormatCurrency } from "@services/IntlNumberFormatCurrency";

import { IStore } from "@app/redux/reducers";

import {
  premisesBooleanEquipments,
  premisesCountableEquipments,
} from "./b2b.factories";
import { TelemetryCalendarContext } from "./b2b.typings";

const getRecommendedDurationFunction = (store: IStore) =>
  store.b2b.get("recommendedDuration");

export const getRecommendedDuration = createSelector(
  getRecommendedDurationFunction,
  f => f,
);

const getRecommendedDurationsAppliedFunction = (store: IStore) =>
  store.b2b.get("recommendedDurationsApplied");

export const getRecommendedDurationsApplied = createSelector(
  getRecommendedDurationsAppliedFunction,
  f => f,
);

const getSurfaceAreaFunction = (store: IStore) => store.b2b.get("surfaceArea");

export const getSurfaceArea = createSelector(getSurfaceAreaFunction, f => f);

const getPremisesEquipmentsFunction = (store: IStore) =>
  store.b2b.get("premisesEquipments");

export const getPremisesEquipments = createSelector(
  getPremisesEquipmentsFunction,
  f => f,
);

const getPremisesFunction = (store: IStore) => store.b2b.get("premises");

export const getPremises = createSelector(getPremisesFunction, f => f);

const getPremisesEquipmentsByTypeFunction = (
  store: IStore,
  premisesType: PREMISES,
): Record<string, number | boolean> => {
  const filteredEquipments = Object.entries(
    store.b2b.get("premisesEquipments").toJS(),
  ).reduce((acc: Record<string, number | boolean>, item) => {
    const [key, val] = item;

    if (
      key.includes("number") &&
      premisesCountableEquipments[premisesType].includes(
        key as PREMISES_COUNTABLE_EQUIPMENTS_OPTIONS,
      )
    ) {
      acc[key] = val;
    }

    if (
      key.includes("has") &&
      premisesBooleanEquipments[premisesType].includes(
        key as PREMISES_BOOLEAN_EQUIPMENTS_OPTIONS,
      )
    ) {
      acc[key] = val;
    }

    return acc;
  }, {});

  return filteredEquipments;
};

export const getPremisesEquipmentsByType = createSelector(
  getPremisesEquipmentsByTypeFunction,
  f => f,
);

const getTaskFunction = (store: IStore, service: EXTRA_SERVICES_B2B) =>
  store.b2b.getIn(["tasks", service]);

export const getTask = createSelector(getTaskFunction, f => f);

const getPremisesServiceFunction = (
  store: IStore,
  service: EXTRA_SERVICES_B2B,
) => store.b2b.getIn(["premisesServices", store.b2b.get("premises"), service]);

export const getPremisesService = createSelector(
  getPremisesServiceFunction,
  f => f,
);

const getTasksFunction = (store: IStore) => Map(store.b2b.get("tasks"));

export const getTasks = createSelector(getTasksFunction, f => f);

const getSelectedCleaningFrequencyFunction = (store: IStore) =>
  store.b2b.get("frequency");

export const getSelectedCleaningFrequency = createSelector(
  getSelectedCleaningFrequencyFunction,
  f => f,
);

const getHasCleaningMaterialsFunction = (store: IStore) =>
  store.b2b.get("hasCleaningMaterials");

export const getHasCleaningMaterials = createSelector(
  getHasCleaningMaterialsFunction,
  f => f,
);

const getContactAfterBookingFunction = (store: IStore) =>
  store.b2b.get("contactAfterBooking");

export const getContactAfterBooking = createSelector(
  getContactAfterBookingFunction,
  f => f,
);

const getPricingFunction = (store: IStore) => store.b2b.get("pricing");

export const getPricing = createSelector(getPricingFunction, f => f);

const getTotalCleaningTimeFunction = (store: IStore) =>
  getTasks(store)
    .valueSeq()
    .filter(task => task.get("selected"))
    .reduce((acc: number, task) => acc + task.get("duration"), 0);

export const getTotalCleaningTime = createSelector(
  getTotalCleaningTimeFunction,
  f => f,
);

const getPremisesServicesTotalDurationFunction = (store: IStore) =>
  getPremisesServices(store)
    ?.get(getPremises(store))
    ?.valueSeq()
    .filter((task: ImmutableRecord<Task>) => task.get("selected"))
    .reduce(
      (acc: number, task: ImmutableRecord<Task>) => acc + task.get("duration"),
      0,
    ) || 0;

export const getPremisesServicesTotalDuration = createSelector(
  getPremisesServicesTotalDurationFunction,
  f => f,
);

const getPremisesServicesSelectedFunction = (store: IStore) => {
  const premisesServices = getPremisesServices(store)?.get(getPremises(store));

  if (premisesServices) {
    return mapToList(premisesServices).filter(item => item.get("selected"));
  }

  return List();
};

export const getPremisesServicesSelected = createSelector(
  getPremisesServicesSelectedFunction,
  f => f,
);

const getExecutionFn = (store: IStore) => store.b2b.get("execution");

export const getExecution = createSelector(getExecutionFn, f => f);

const getExecutionDateTimeFn = (store: IStore) => {
  const execution = store.b2b.get("execution");

  if (!execution.date || !execution.time) {
    return null;
  }

  return `${execution.date}T${execution.time}`;
};

export const getExecutionDateTime = createSelector(
  getExecutionDateTimeFn,
  f => f,
);

const getSelectedTasksListFunction = (store: IStore) =>
  mapToList(getTasks(store)).filter(item => item.get("selected"));

export const getSelectedTasksList = createSelector(
  getSelectedTasksListFunction,
  f => f,
);

const getIsExecutionDateTimeValidatedFunction = (store: IStore) =>
  store.b2b.get("isExecutionDateTimeValidated");

export const getIsExecutionDateTimeValidated = createSelector(
  getIsExecutionDateTimeValidatedFunction,
  f => f,
);

const getNonStandardFrequencyShiftsFunction = (store: IStore) =>
  store.b2b.get("nonStandardFrequencyShifts");

export const getNonStandardFrequencyShifts = createSelector(
  getNonStandardFrequencyShiftsFunction,
  f => f,
);

const getServiceLocationIdFunction = (store: IStore) =>
  store.b2b.get("serviceLocationId");

export const getServiceLocationId = createSelector(
  getServiceLocationIdFunction,
  f => f,
);

const getPreviousAgentsFunction = (store: IStore) =>
  store.b2b.get("previousAgents");

export const getPreviousAgents = createSelector(
  getPreviousAgentsFunction,
  f => f,
);

const getPreselectedAgentUuidFunction = (store: IStore) =>
  store.b2b.get("preselectedAgentUuid");

export const getPreselectedAgentUuid = createSelector(
  getPreselectedAgentUuidFunction,
  f => f,
);
const getIsChoosingAnyBatmaidFunction = (store: IStore) =>
  store.b2b.get("isChoosingAnyBatmaid");

export const getIsChoosingAnyBatmaid = createSelector(
  getIsChoosingAnyBatmaidFunction,
  f => f,
);
const getAgentByUuidFunction = (store: IStore, uuid: string) => {
  const result = getPreviousAgents(store).findEntry(
    item => item.get("uuid") === uuid,
  );
  return result ? result[1] : undefined;
};

export const getAgentByUuid = createSelector(getAgentByUuidFunction, f => f);

const getRegistrationFormFunction = (store: IStore) => store.b2b.get("form");

export const getRegistrationForm = createSelector(
  getRegistrationFormFunction,
  f => f,
);

const getLocationUuidFunction = (store: IStore) =>
  store.b2b.get("locationUuid");

export const getLocationUuid = createSelector(getLocationUuidFunction, f => f);

const getAgentPreferenceFunction = (store: IStore) =>
  store.b2b.get("agentPreference");

export const getAgentPreference = createSelector(
  getAgentPreferenceFunction,
  f => f,
);

const getReservationUuidFunction = (store: IStore) =>
  store.b2b.get("reservationUuid");

export const getReservationUuid = createSelector(
  getReservationUuidFunction,
  f => f,
);

const getPetsInfoFunction = (store: IStore) => store.b2b.get("hasPets");

export const getPetsInfo = createSelector(getPetsInfoFunction, f => f);

const getWeekStartDateFunction = (store: IStore) =>
  store.b2b.get("calendar").get("weekStartDate");

export const getWeekStartDate = createSelector(
  getWeekStartDateFunction,
  f => f,
);

const getBookingAvailabilityFunction = (store: IStore) =>
  store.b2b.get("calendar");

export const getBookingAvailability = createSelector(
  getBookingAvailabilityFunction,
  f => f,
);

const getCalendarWeekFunction = (store: IStore, week: CALENDAR_WEEK_SHIFT) => {
  const weekStartDate = store.b2b.get("calendar").get("weekStartDate");
  const currentWeek = parseISO(weekStartDate);

  return week === CALENDAR_WEEK_SHIFT.PREVIOUS
    ? format(subDays(currentWeek, 7), DATE_FORMAT.YEAR_MONTH_DAY)
    : format(addDays(currentWeek, 7), DATE_FORMAT.YEAR_MONTH_DAY);
};

export const getCalendarWeek = createSelector(getCalendarWeekFunction, f => f);

const getPremisesServicesFunction = (store: IStore) =>
  store.b2b.get("premisesServices");

export const getPremisesServices = createSelector(
  getPremisesServicesFunction,
  f => f,
);

const getPremisesServicesFetchedFunction = (store: IStore) =>
  store.b2b.get("premisesServicesFetched");

export const getPremisesServicesFetched = createSelector(
  getPremisesServicesFetchedFunction,
  f => f,
);

const getSpecialInstructionsFunction = (store: IStore) =>
  store.b2b.get("specialInstructions");

export const getSpecialInstructions = createSelector(
  getSpecialInstructionsFunction,
  f => f,
);

const getKeyPickFunction = (store: IStore) => store.b2b.get("keyPick");

export const getKeyPick = createSelector(getKeyPickFunction, f => f);

const getLocationCommentFunction = (store: IStore) =>
  store.b2b.get("locationComment");

export const getLocationComment = createSelector(
  getLocationCommentFunction,
  f => f,
);

const getAvailablePaymentMethodsFunction = (store: IStore) =>
  store.b2b.get("availablePaymentMethods");

export const getAvailablePaymentMethods = createSelector(
  getAvailablePaymentMethodsFunction,
  f => f,
);

const getSelectedPaymentMethodFunction = (store: IStore) =>
  store.b2b.get("selectedPaymentMethod");

export const getSelectedPaymentMethod = createSelector(
  getSelectedPaymentMethodFunction,
  f => f,
);

const getLocationsFunction = (store: IStore) => store.b2b.get("locations");

export const getLocations = createSelector(getLocationsFunction, f => f);

const getVoucherFunction = (store: IStore) => store.b2b.get("voucherToken");

export const getVoucher = createSelector(getVoucherFunction, f => f);

const getVoucherMessageFunction = (store: IStore) =>
  store.b2b.get("voucherMessage");

export const getVoucherMessage = createSelector(
  getVoucherMessageFunction,
  f => f,
);

const getVoucherStatusFunction = (store: IStore) =>
  store.b2b.get("voucherStatus");

export const getVoucherStatus = createSelector(
  getVoucherStatusFunction,
  f => f,
);

const getQueryParamsFunction = (store: IStore) => store.b2b.get("queryParams");

export const getQueryParams = createSelector(getQueryParamsFunction, f => f);

const getIDontKnowDurationNotificationFn = (store: IStore) =>
  store.b2b.get("iDontKnowDurationNotification");

export const getIDontKnowDurationNotification = createSelector(
  getIDontKnowDurationNotificationFn,
  f => f,
);

const getIDontKnowFrequencyNotificationFn = (store: IStore) =>
  store.b2b.get("iDontKnowFrequencyNotification");

export const getIDontKnowFrequencyNotification = createSelector(
  getIDontKnowFrequencyNotificationFn,
  f => f,
);

const getAdditionalInfoFn = (store: IStore) => store.b2b.get("additionalInfo");

export const getAdditionalInfo = createSelector(getAdditionalInfoFn, f => f);

const getB2BLocationsLoadingStateFn = (store: IStore) =>
  store.b2b.get("locationsLoadingState");
export const getB2BLocationsLoadingState = createSelector(
  getB2BLocationsLoadingStateFn,
  fn => fn,
);

const getAvailabilityLoadingStateFn = (store: IStore) =>
  store.b2b.get("availabilityLoadingState");

export const getAvailabilityLoadingState = createSelector(
  getAvailabilityLoadingStateFn,
  f => f,
);

const getTelemetryCalendarContextFunction = (
  store: IStore,
): TelemetryCalendarContext => {
  const frequency = getSelectedCleaningFrequency(store);
  const nonStandardFrequencyShiftsDuration = getNonStandardFrequencyShifts(
    store,
  ).reduce((timeInWeek, shift) => timeInWeek + shift.duration, 0);
  const totalCleaningTime = getTotalCleaningTime(store);
  const premisesServicesTotalDuration = getPremisesServicesTotalDuration(store);
  const { date } = getExecution(store);
  const duration =
    frequency === FREQUENCY.MORE_OFTEN
      ? nonStandardFrequencyShiftsDuration
      : totalCleaningTime + premisesServicesTotalDuration;

  const availability = getBookingAvailability(store);
  const hoursList = date ? availability.getIn(["hours", date]) : null;

  const displayedSlots = hoursList
    ? hoursList.valueSeq().reduce((acc: string[], item: HourItem) => {
        if (item.availableAgentsRemaining) {
          return [...acc, `${date} ${item.duration}`];
        }
        return acc;
      }, [])
    : [];

  return {
    frequency,
    duration,
    displayedSlots,
  };
};

export const getTelemetryCalendarContext = createSelector(
  getTelemetryCalendarContextFunction,
  f => f,
);

const getPricingLoadingStateFn = (store: IStore) =>
  store.b2b.get("pricingLoadingState");

export const getPricingLoadingState = createSelector(
  getPricingLoadingStateFn,
  f => f,
);
const getCalendarCustomDaysFn = (store: IStore) =>
  store.b2b.get("calendar").get("customDays");

export const getCalendarCustomDays = createSelector(
  getCalendarCustomDaysFn,
  f => f,
);

const getFrequenciesFn = (store: IStore) => store.b2b.get("frequencies");

export const getFrequencies = createSelector(getFrequenciesFn, f => f);

const getFrequenciesLoadingStateFn = (store: IStore) =>
  store.b2b.get("frequenciesLoadingState");

export const getFrequenciesLoadingState = createSelector(
  getFrequenciesLoadingStateFn,
  f => f,
);

type PricesForFrequencies = Partial<
  Record<
    FREQUENCY,
    {
      uuid: string;
      price: string;
    }
  >
>;

const getPricesForFrequenciesSelectorFn = (
  store: IStore,
): PricesForFrequencies => {
  const pricing = store.b2b.get("pricePlans");
  const data: PricesForFrequencies = {};

  pricing
    .toJS()
    .map(({ frequency, uuid, pricePerHour, currency }: PricePlan) => {
      data[frequency as FREQUENCY] = {
        uuid,
        price: intlNumberFormatCurrency(pricePerHour, currency),
      };
    });

  return data;
};

export const getPricesForFrequenciesSelector = createSelector(
  getPricesForFrequenciesSelectorFn,
  f => f,
);

const getSelectedPricePlanUuidFunction = (store: IStore) =>
  store.b2b.get("pricePlanUuid");

export const getSelectedPricePlanUuid = createSelector(
  getSelectedPricePlanUuidFunction,
  f => f,
);
